import { Button, Card, Space, Tag, Typography } from 'antd';
import { Plus, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useActiveLanguages } from '@features/languages/useLanguages';
import { contentsApi, type ContentVariant, type ContentVariantType } from '@api/contents';
import type { Language } from '@features/languages/types';

const { Text } = Typography;

interface VariantPanelProps {
  /** Düzenlenen içeriğin dil id'si. */
  currentLanguageId: string;
  /** İçeriğin ait olduğu çeviri grubunda mevcut dil KODLARI (BE variantLanguageCodes). */
  variantLanguageCodes: string[];
  /** Bir dilde yeni varyant ekleme isteği (aynı groupId, farklı languageId). */
  onAddVariant: (language: Language) => void;
  /** Yalnız görüntüleme (yeni içerik veya yetkisiz) — ekle butonları gizlenir. */
  readOnly?: boolean;
  /**
   * K-13 #20 — hızlı dil-atlama. `contentType` + `groupId` + `onOpenVariant`
   * birlikte verildiğinde kardeş varyantlar
   * `contents/{type}/groups/{groupId}/variants` endpoint'inden çekilir ve
   * mevcut diller doğrudan navigasyon çipine dönüşür. Verilmezse panel eski
   * (salt gösterge) davranışını korur.
   */
  contentType?: ContentVariantType;
  groupId?: string;
  onOpenVariant?: (variantId: string) => void;
}

/**
 * Çeviri-varyant paneli. Her varyant AYRI bir içerik entity'sidir (aynı groupId,
 * farklı languageId) — bu yüzden dil-sekmesi (tek entity yanılgısı) değil, kardeş
 * içerikleri listeleyen bir panel. Mevcut varyantları listeler (K-13 #20 ile
 * tıklanabilir dil-atlama çipleri), eksik dillere "varyant ekle" sunar.
 */
export function VariantPanel({
  currentLanguageId,
  variantLanguageCodes,
  onAddVariant,
  readOnly,
  contentType,
  groupId,
  onOpenVariant,
}: VariantPanelProps) {
  const { t } = useTranslation('common');
  const { data: languages = [] } = useActiveLanguages();
  const existing = new Set(variantLanguageCodes.map((c) => c.toLowerCase()));

  const jumpEnabled = Boolean(contentType && groupId && onOpenVariant);
  const { data: siblings = [] } = useQuery({
    queryKey: ['content-variants', contentType, groupId],
    // jumpEnabled guard'ı contentType/groupId'nin tanımlı olduğunu garantiler.
    queryFn: () => contentsApi.variants(contentType ?? 'page', groupId ?? ''),
    enabled: jumpEnabled,
    staleTime: 30_000,
  });

  const siblingFor = (lang: Language): ContentVariant | undefined =>
    siblings.find(
      (v) =>
        v.languageId === lang.id ||
        (v.languageCode ?? '').toLowerCase() === lang.code.toLowerCase(),
    );

  return (
    <Card size="small" title={t('contentLanguage.variantsTitle')}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {languages.map((lang) => {
          const isCurrent = lang.id === currentLanguageId;
          const hasVariant = existing.has(lang.code.toLowerCase());
          const sibling = !isCurrent && hasVariant ? siblingFor(lang) : undefined;
          // Generated DTO'da id opsiyonel — navigasyon için somut id şart.
          const siblingId = sibling?.id;
          return (
            <div
              key={lang.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 13 }}>
                <Text code style={{ marginRight: 6 }}>
                  {lang.code}
                </Text>
                {lang.name}
              </span>
              {isCurrent ? (
                <Tag color="blue">{t('contentLanguage.variantCurrent')}</Tag>
              ) : hasVariant ? (
                sibling && siblingId && onOpenVariant ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<ExternalLink size={13} />}
                    title={sibling.title ?? sibling.slug ?? undefined}
                    onClick={() => onOpenVariant(siblingId)}
                  >
                    {t('contentLanguage.variantOpen')}
                  </Button>
                ) : (
                  <Tag color="green">{t('contentLanguage.variantExists')}</Tag>
                )
              ) : readOnly ? (
                <Tag>{t('contentLanguage.variantMissing')}</Tag>
              ) : (
                <Button
                  type="link"
                  size="small"
                  icon={<Plus size={13} />}
                  onClick={() => onAddVariant(lang)}
                >
                  {t('contentLanguage.addVariant')}
                </Button>
              )}
            </div>
          );
        })}
      </Space>
    </Card>
  );
}
